import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useFavorites } from "../hooks/useFavorites";
import { useStudies } from "../hooks/useStudies";
import * as XLSX from "xlsx";

const SEARCH_STATE_KEY = "sbdb_search_state";

function loadSearchState() {
  try { return JSON.parse(sessionStorage.getItem(SEARCH_STATE_KEY)) || {}; } catch { return {}; }
}
function saveSearchState(query, filters) {
  try { sessionStorage.setItem(SEARCH_STATE_KEY, JSON.stringify({ query, filters })); } catch {}
}

export default function Search() {
  const { studies, loading: studiesLoading } = useStudies();
  const [filtered, setFiltered] = useState([]);
  const [expandedTitles, setExpandedTitles] = useState({});
  const [selectedTitles, setSelectedTitles] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const saved = loadSearchState();
  const [query, setQuery] = useState(saved.query || "");
  const [debouncedQuery, setDebouncedQuery] = useState(saved.query || "");
  const [filters, setFilters] = useState({
    category: "",
    journal: "",
    author: "",
    microbiota: "",
    epilepsyType: "",
    yearFrom: "",
    yearTo: "",
    boolMode: "AND",
    ...(saved.filters || {}),
  });
  const [currentPage, setCurrentPage] = useState(1);
  const studiesPerPage = 10;

  // Debounce the search query by 250ms
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 250);
    return () => clearTimeout(t);
  }, [query]);

  // Persist search state so it survives back-navigation
  useEffect(() => {
    saveSearchState(query, filters);
  }, [query, filters]);

  useEffect(() => {
    const lower = (val) => (val || "").toLowerCase();
    const boolMode = filters.boolMode;
    const results = studies.filter((study) => {
      const matchQuery =
        !debouncedQuery ||
        Object.values(study).some((val) =>
          lower(String(val)).includes(lower(debouncedQuery))
        );
      const matchCategory =
        !filters.category ||
        (study.Age_category &&
          (() => {
            const age = lower(study.Age_category);
            const selected = lower(filters.category);
            return (
              age === selected ||
              (selected === "pediatric" && age === "child")
            );
          })());
      const matchJournals =
        !filters.journal ||
        (study.Journal && lower(study.Journal).includes(lower(filters.journal)));
      const matchAuthor =
        !filters.author ||
        (study.Authors &&
          lower(study.Authors).includes(lower(filters.author)));
      const microText = [
        study.Microbiota,
        study.Comparison,
        study.Description,
        study.Increased,
        study.Decreased,
      ]
        .filter(Boolean)
        .map(lower)
        .join(" ");
      const matchMicrobiota =
        !filters.microbiota || microText.includes(lower(filters.microbiota));
      const matchEpilepsyType =
        !filters.epilepsyType ||
        normalizeEpilepsyType(study.Epilepsy_type).includes(normalizeEpilepsyType(filters.epilepsyType));
      const matchYearFrom =
        !filters.yearFrom ||
        (study.Year_of_publication &&
          Number(study.Year_of_publication) >= Number(filters.yearFrom));
      const matchYearTo =
        !filters.yearTo ||
        (study.Year_of_publication &&
          Number(study.Year_of_publication) <= Number(filters.yearTo));
      // Boolean logic
      if (boolMode === "AND") {
        return (
          matchQuery &&
          matchCategory &&
          matchJournals &&
          matchAuthor &&
          matchMicrobiota &&
          matchEpilepsyType &&
          matchYearFrom &&
          matchYearTo
        );
      } else {
        return (
          matchQuery ||
          matchCategory ||
          matchJournals ||
          matchAuthor ||
          matchMicrobiota ||
          matchEpilepsyType ||
          matchYearFrom ||
          matchYearTo
        );
      }
    });
    setFiltered(results);
  }, [debouncedQuery, filters, studies]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedQuery, filters]);

  const { isFavorite, toggleFavorite } = useFavorites();
  const sanitize = (val) => (val && val !== "nan" ? val : "N/A");
  const normalizeEpilepsyType = (val) => (val || "").toLowerCase().replace(/-/g, " ").trim();

  const toggleSelectTitle = (title) => {
    setSelectedTitles(prev => {
      const next = new Set(prev);
      next.has(title) ? next.delete(title) : next.add(title);
      return next;
    });
  };

  const toggleSelectId = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const uniqueTitles = Array.from(new Set(filtered.map(study => sanitize(study.Article_title))));
  const totalUnique = uniqueTitles.length;
  const indexOfLastStudy = currentPage * studiesPerPage;
  const indexOfFirstStudy = indexOfLastStudy - studiesPerPage;
  const currentTitles = uniqueTitles.slice(indexOfFirstStudy, indexOfLastStudy);

  const allPageTitles = currentTitles;
  const allPageSelected = allPageTitles.length > 0 && allPageTitles.every(t => selectedTitles.has(t));

  const toggleSelectAll = () => {
    if (allPageSelected) {
      setSelectedTitles(prev => {
        const next = new Set(prev);
        allPageTitles.forEach(t => next.delete(t));
        return next;
      });
    } else {
      setSelectedTitles(prev => {
        const next = new Set(prev);
        allPageTitles.forEach(t => next.add(t));
        return next;
      });
    }
  };

  const parseTaxa = (str) =>
    (str || "").split(",").map(t => t.trim()).filter(t => t && !["nan", "n/a", "na", "--", "—"].includes(t.toLowerCase()));

  const exportToExcel = () => {
    const exportSet = filtered.filter(s => selectedTitles.has(sanitize(s.Article_title)));
    const columns = [
      "Article_title", "Authors", "Journal", "Year_of_publication", "DOI",
      "Study_type", "Sample_type", "Host", "Epilepsy_type", "Age_category",
      "Country", "Group_A", "Group_B",
      "#_participants_in_group_A", "#_participants_in_group_B", "Sample_size_Patients",
      "Method", "DNA_isolation_kit", "Sequencing_platform", "Sequencing_primers", "Sequencing_region",
      "Data_accession_ID", "Data_accession_link",
      "Comparison", "Increased", "Decreased",
    ];
    const headers = [
      "Article Title", "Authors", "Journal", "Year", "DOI",
      "Study Type", "Sample Type", "Host", "Epilepsy Type", "Age Category",
      "Country", "Group A", "Group B",
      "# Participants (A)", "# Participants (B)", "Sample Size (Patients)",
      "Method", "DNA Isolation Kit", "Sequencing Platform", "Sequencing Primers", "Sequencing Region",
      "Data Accession ID", "Data Accession Link",
      "Comparison", "Increased Taxa", "Decreased Taxa",
    ];
    const rows = exportSet.map(s => columns.map(col => {
      const v = s[col] ?? "";
      return col === "Year_of_publication" ? String(v) : v;
    }));
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws["!cols"] = headers.map((h, i) => ({ wch: i === 0 ? 60 : i < 4 ? 25 : 20 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Studies");
    XLSX.writeFile(wb, `SeizureBiomeDB_export_${exportSet.length}_studies.xlsx`);
  };

  const generateComparisonExcel = () => {
    const compRows = selectedIds.size >= 2
      ? studies.filter(s => selectedIds.has(s.id))
      : studies.filter(s => selectedTitles.has(sanitize(s.Article_title)));

    // Collect all unique taxa across all selected comparisons
    const allTaxaSet = new Set();
    compRows.forEach(row => {
      parseTaxa(row.Increased).forEach(t => allTaxaSet.add(t));
      parseTaxa(row.Decreased).forEach(t => allTaxaSet.add(t));
    });
    const allTaxa = Array.from(allTaxaSet).sort();

    // Column label: article title truncated + comparison type
    const colLabel = (s) => {
      const title = (s.Article_title || "Untitled").slice(0, 40) + ((s.Article_title || "").length > 40 ? "…" : "");
      return s.Comparison ? `${title}\n(${s.Comparison})` : title;
    };

    const sheetData = [
      ["Study",         ...compRows.map(s => s.Article_title || "Untitled")],
      ["Host",          ...compRows.map(s => s.Host || "—")],
      ["Epilepsy Type", ...compRows.map(s => s.Epilepsy_type || "—")],
      ["Comparison",    ...compRows.map(s => s.Comparison || "—")],
      ["Authors",       ...compRows.map(s => s.Authors || "—")],
      ["Year",          ...compRows.map(s => String(s.Year_of_publication || "—"))],
      [""], // blank separator row
      ...allTaxa.map(taxa => {
        const cells = compRows.map(row => {
          const inInc = parseTaxa(row.Increased).some(t => t.toLowerCase() === taxa.toLowerCase());
          const inDec = parseTaxa(row.Decreased).some(t => t.toLowerCase() === taxa.toLowerCase());
          if (inInc && inDec) return "↑↓";
          if (inInc) return "↑";
          if (inDec) return "↓";
          return "";
        });
        return [taxa, ...cells];
      }),
    ];

    const ws = XLSX.utils.aoa_to_sheet(sheetData);
    // Column widths
    ws["!cols"] = sheetData[0].map((_, i) => ({ wch: i === 0 ? 40 : 32 }));
    // Style header rows (rows 0–5) — bold via cell format isn't supported in xlsx community edition,
    // but we can freeze the pane and set row heights for clarity
    ws["!freeze"] = { xSplit: 1, ySplit: 7 }; // freeze taxa label column + header rows

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cross-Study Comparison");
    XLSX.writeFile(wb, `SeizureBiomeDB_cross_comparison_${compRows.length}_comparisons.xlsx`);
  };

  // Group all filtered studies by title
  const groupedFiltered = filtered.reduce((acc, study) => {
    const title = sanitize(study.Article_title);
    if (!acc[title]) acc[title] = [];
    acc[title].push(study);
    return acc;
  }, {});
  // Only show the groups for the current page's titles
  const groupedCurrent = currentTitles.reduce((acc, title) => {
    if (groupedFiltered[title]) acc[title] = groupedFiltered[title];
    return acc;
  }, {});
  const toggleExpand = (title) => {
    setExpandedTitles((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };
  const clearFilters = () => {
    setFilters({
      category: "",
      journal: "",
      author: "",
      microbiota: "",
      epilepsyType: "",
      yearFrom: "",
      yearTo: "",
      boolMode: "AND",
    });
  };
  const uniqueJournals = [
    ...new Set(
      studies.map((s) => sanitize(s.Journal)).filter((j) => j && j !== "N/A")
    ),
  ];
  const categories = ["Pediatric", "Adult", "Animal"];
  const epilepsyTypeMap = new Map();
  studies.forEach(s => {
    const orig = s.Epilepsy_type;
    if (!orig || orig === "nan") return;
    const norm = normalizeEpilepsyType(orig);
    if (!epilepsyTypeMap.has(norm)) epilepsyTypeMap.set(norm, orig);
  });
  const epilepsyTypes = Array.from(epilepsyTypeMap.values());
  const activeTags = Object.entries(filters)
    .filter(([key, v]) =>
      key === "journal" ? v.length > 0 : v && key !== "boolMode"
    )
    .map(([key, value]) => ({ key, value }));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  return (
    <div
      style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        display: "flex",
        flexDirection: "row",
        padding: "2rem 1rem",
        gap: "2.5rem",
        maxWidth: "1400px",
        margin: "0 auto",
        flexWrap: "wrap",
        fontSize: "16px",
        lineHeight: "1.6",
        color: "#333",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
      }}
    >
      {/* Sidebar */}
      <div
        style={{
          flex: "0 0 320px",
          background: "#fff",
          borderRadius: "16px",
          padding: "2rem 1.5rem",
          boxShadow: "0 6px 24px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e5e7eb",
          height: "fit-content",
          position: "sticky",
          top: "2rem",
          minWidth: "260px",
          marginBottom: "2rem",
        }}
      >
        <h3
          style={{
            color: "#0079C0",
            marginBottom: "1.5rem",
            fontSize: "1.5rem",
            fontWeight: "700",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          Filters
        </h3>

        {[
          { label: "Category", type: "select", options: categories, key: "category"},
          { label: "Journal", type: "select", options: uniqueJournals, key: "journal"},
          { label: "Epilepsy Type", type: "select", options: epilepsyTypes, key: "epilepsyType"},
          { label: "Author", type: "input", key: "author", placeholder: "e.g., Smith"},
          { label: "Microbiota", type: "input", key: "microbiota", placeholder: "e.g., Lactobacillus"},
          { label: "Publication Year", type: "year-range", keyFrom: "yearFrom", keyTo: "yearTo" },
          { label: "Combine Filters", type: "toggle", key: "boolMode"},
        ].map(({ label, type, key, options, placeholder, icon }) => (
          <div 
            key={key} 
            style={{ marginBottom: "1.5rem" }}
          >
            <label style={{ 
              fontWeight: "600", 
              fontSize: "0.95rem",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              marginBottom: "0.5rem",
              color: "#374151",
            }}>
              {icon} {label}
            </label>
            {type === "select" ? (
              <select
                value={filters[key]}
                onChange={(e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }))}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  marginTop: "0.25rem",
                  fontSize: "0.9rem",
                  background: "white",
                  transition: "all 0.3s ease",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#0079C0";
                  e.target.style.boxShadow = "0 0 0 3px rgba(0, 121, 192, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#d1d5db";
                  e.target.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
                }}
              >
                <option value="">All Categories</option>
                {options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </option>
                ))}
              </select>
            ) : type === "multi-select" ? (
              <select
                value={filters[key]}
                onChange={(e) => setFilters((prev) => ({ ...prev, [key]: Array.from(e.target.selectedOptions, (option) => option.value) }))}
                multiple
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  marginTop: "0.25rem",
                  fontSize: "0.9rem",
                  background: "white",
                  transition: "all 0.3s ease",
                  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#0079C0";
                  e.target.style.boxShadow = "0 0 0 3px rgba(0, 121, 192, 0.1)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "#d1d5db";
                  e.target.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
                }}
              >
                <option value="">Select Journals</option>
                {options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </option>
                ))}
                </select>
               ) : type === "year-range" ? (
              <div style={{ display: "flex", alignItems: "center" }}>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  value={filters.yearFrom}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, yearFrom: e.target.value }))
                  }
                  placeholder="From"
                  style={{
                    width: "45%",               
                    padding: "0.75rem",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontSize: "0.85rem",          
                    background: "white",
                    transition: "all 0.3s ease",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#0079C0";
                    e.target.style.boxShadow = "0 0 0 3px rgba(0, 121, 192, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d5db";
                    e.target.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
                  }}
                />
                <span style={{ margin: "0 0.75rem" }}>to</span>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  value={filters.yearTo}
                  onChange={(e) =>
                    setFilters((prev) => ({ ...prev, yearTo: e.target.value }))
                  }
                  placeholder="To"
                  style={{
                    width: "45%",                 
                    padding: "0.75rem",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    fontSize: "0.85rem",
                    background: "white",
                    transition: "all 0.3s ease",
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#0079C0";
                    e.target.style.boxShadow = "0 0 0 3px rgba(0, 121, 192, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d1d5db";
                    e.target.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
                  }}
                />
              </div>
            )             
             : type === "toggle" ? (
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                <input
                  type="radio"
                  id={`${key}-and`}
                  name={key}
                  value="AND"
                  checked={filters.boolMode === "AND"}
                  onChange={() => setFilters((prev) => ({ ...prev, boolMode: "AND" }))}
                  style={{ marginRight: "0.5rem" }}
                />
                <label htmlFor={`${key}-and`}>AND</label>
                <input
                  type="radio"
                  id={`${key}-or`}
                  name={key}
                  value="OR"
                  checked={filters.boolMode === "OR"}
                  onChange={() => setFilters((prev) => ({ ...prev, boolMode: "OR" }))}
                  style={{ marginLeft: "1rem", marginRight: "0.5rem" }}
                />
                <label htmlFor={`${key}-or`}>OR</label>
              </div>
          ) : (
            <input
              type="text"
              value={filters[key]}
              placeholder={placeholder}
              onChange={(e) => setFilters((prev) => ({ ...prev, [key]: e.target.value }))}
              style={{
                width: (key === "author" || key === "microbiota") ? "92%" : "100%",
                display: "block",         
                padding: "0.75rem",
                borderRadius: "8px",
                border: "1px solid #d1d5db",
                marginTop: "0.25rem",
                fontSize: (key === "author" || key === "microbiota") ? "0.85rem" : "0.9rem",
                background: "white",
                transition: "all 0.3s ease",
                boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
              }}
              onFocus={(e) => {
                e.target.style.borderColor = "#0079C0";
                e.target.style.boxShadow = "0 0 0 3px rgba(0, 121, 192, 0.1)";
              }}
              onBlur={(e) => {
                e.target.style.borderColor = "#d1d5db";
                e.target.style.boxShadow = "0 1px 3px rgba(0, 0, 0, 0.1)";
              }}
            />
          )}
          </div>
        ))}
        <button
          onClick={clearFilters}
          style={{
            width: "100%",
            background: "#0079C0",
            color: "white",
            padding: "0.875rem",
            borderRadius: "8px",
            border: "none",
            cursor: "pointer",
            fontWeight: "600",
            fontSize: "0.95rem",
            marginTop: "1rem",
          }}
        >
          Clear All Filters
        </button>
      </div>
      {/* Main Results Area */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 6px 24px rgba(0, 0, 0, 0.08)",
          padding: "2.5rem 2rem 2rem 2rem",
          border: "1px solid #e5e7eb",
          marginBottom: "2rem",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Search Bar */}
        <div style={{ marginBottom: "2rem", display: "flex", alignItems: "center", gap: "1rem" }}>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === "Escape") { setQuery(""); setDebouncedQuery(""); } }}
            placeholder="Search by keyword, author, taxa, epilepsy type…  (Esc to clear)"
            style={{
              flex: 1,
              padding: "1rem 1.2rem",
              borderRadius: "10px",
              border: "1px solid #d1d5db",
              fontSize: "1.1rem",
              background: "#f8fafc",
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
              outline: "none",
              transition: "border 0.2s, box-shadow 0.2s",
            }}
            onFocus={e => {
              e.target.style.borderColor = "#0079C0";
              e.target.style.boxShadow = "0 0 0 3px rgba(0, 121, 192, 0.08)";
            }}
            onBlur={e => {
              e.target.style.borderColor = "#d1d5db";
              e.target.style.boxShadow = "0 1px 3px rgba(0,0,0,0.04)";
            }}
          />
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem", flexWrap: "wrap", gap: "0.75rem" }}>
          <div>
            <h2 style={{ color: "#0079C0", fontSize: "1.6rem", fontWeight: "700", marginBottom: "0.2rem" }}>
              {studiesLoading ? "Loading…" : `${totalUnique} stud${totalUnique === 1 ? "y" : "ies"} · ${filtered.length} comparison${filtered.length === 1 ? "" : "s"}`}
            </h2>
            <p style={{ fontSize: "0.8rem", color: "#94a3b8", margin: 0 }}>
              Expand a study to see comparisons. Check individual comparisons to export a cross-study matrix.
            </p>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
            {selectedTitles.size > 0 && (
              <button
                onClick={exportToExcel}
                style={{
                  background: "#10b981",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "0.5rem 1rem",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "0.85rem",
                  whiteSpace: "nowrap",
                }}
              >
                Export {selectedTitles.size} Selected (.xlsx)
              </button>
            )}
            {(selectedIds.size >= 2 || selectedTitles.size >= 2) && (
              <button
                onClick={generateComparisonExcel}
                style={{
                  background: "#0079C0",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  padding: "0.5rem 1rem",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "0.85rem",
                  whiteSpace: "nowrap",
                }}
              >
                {selectedIds.size >= 2
                  ? `Compare ${selectedIds.size} Comparisons (.xlsx)`
                  : `Compare ${selectedTitles.size} Studies (.xlsx)`}
              </button>
            )}
          </div>
        </div>

        {/* Select-all row */}
        {filtered.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", marginBottom: "1rem", padding: "0.5rem 0.75rem", background: "rgba(0,121,192,0.05)", borderRadius: "8px", border: "1px solid rgba(0,121,192,0.1)" }}>
            <input
              type="checkbox"
              id="select-all"
              checked={allPageSelected}
              onChange={toggleSelectAll}
              style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#0079C0" }}
            />
            <label htmlFor="select-all" style={{ fontSize: "0.875rem", color: "#374151", cursor: "pointer", userSelect: "none" }}>
              {allPageSelected ? "Deselect" : "Select"} all on this page
              {selectedTitles.size > 0 && (
                <span style={{ marginLeft: "0.5rem", color: "#0079C0", fontWeight: 600 }}>
                  · {selectedTitles.size} selected total
                </span>
              )}
            </label>
            <div style={{ marginLeft: "auto", display: "flex", gap: "0.75rem" }}>
              {selectedTitles.size > 0 && (
                <button
                  onClick={() => setSelectedTitles(new Set())}
                  style={{ fontSize: "0.8rem", color: "#6b7280", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                >
                  Clear selection
                </button>
              )}
              {selectedIds.size > 0 && (
                <button
                  onClick={() => setSelectedIds(new Set())}
                  style={{ fontSize: "0.8rem", color: "#0079C0", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                >
                  Clear {selectedIds.size} comparison{selectedIds.size !== 1 ? "s" : ""}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Enhanced Active Tags */}
        {activeTags.length > 0 && (
          <div 
            style={{ 
              display: "flex", 
              flexWrap: "wrap", 
              gap: "0.75rem", 
              marginBottom: "1.5rem" 
            }}
          >
            {activeTags.map(({ key, value }) => (
              <span
                key={key}
                style={{
                  background: "linear-gradient(135deg, #0079C0 0%, #005f9e 100%)",
                  color: "white",
                  padding: "0.5rem 1rem",
                  borderRadius: "20px",
                  fontSize: "0.85rem",
                  fontWeight: "500",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  boxShadow: "0 2px 8px rgba(0, 121, 192, 0.3)",
                }}
              >
                {key}: {value}
                <button
                  onClick={() => setFilters(prev => ({ ...prev, [key]: "" }))}
                  style={{
                    background: "rgba(255, 255, 255, 0.2)",
                    border: "none",
                    borderRadius: "50%",
                    width: "20px",
                    height: "20px",
                    color: "white",
                    cursor: "pointer",
                    fontSize: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}

        {/* Empty state */}
        {!studiesLoading && totalUnique === 0 && (
          <div style={{ textAlign: "center", padding: "3rem 1rem", color: "#64748b" }}>
            <p style={{ fontSize: "1.1rem", fontWeight: 600, marginBottom: "0.5rem" }}>No studies match your search</p>
            <p style={{ fontSize: "0.9rem", marginBottom: "1rem" }}>Try removing a filter or broadening your keyword</p>
            <button onClick={clearFilters} style={{ background: "#0079C0", color: "#fff", border: "none", borderRadius: 6, padding: "0.5rem 1.25rem", cursor: "pointer", fontWeight: 600, fontSize: "0.875rem" }}>
              Clear all filters
            </button>
          </div>
        )}

        {/* Search Results */}
        <AnimatePresence>
          {Object.entries(groupedCurrent).map(([title, studies], index) => (
            <div
              key={title}
              style={{
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(20px)",
                borderRadius: "12px",
                padding: "1.5rem",
                marginBottom: "1rem",
                boxShadow: "0 4px 15px rgba(0, 0, 0, 0.08)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                transition: "all 0.3s ease",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.75rem" }}>
                <input
                  type="checkbox"
                  checked={selectedTitles.has(title)}
                  onChange={() => toggleSelectTitle(title)}
                  style={{ marginTop: "0.3rem", width: 16, height: 16, flexShrink: 0, cursor: "pointer", accentColor: "#0079C0" }}
                  title="Select for export"
                />
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: "1.1rem",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0.5rem",
                    lineHeight: "1.4",
                  }}>
                    {title}
                  </h3>
                  <p style={{
                    fontSize: "0.9rem",
                    color: "#6b7280",
                    marginBottom: "0.75rem",
                  }}>
                    {studies.length} comparison type{studies.length !== 1 ? "s" : ""}
                  </p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexShrink: 0 }}>
                  <button
                    onClick={() => toggleFavorite(title)}
                    title={isFavorite(title) ? "Remove from favourites" : "Add to favourites"}
                    style={{
                      background: isFavorite(title) ? "rgba(239,68,68,0.08)" : "rgba(0,121,192,0.08)",
                      border: isFavorite(title) ? "1px solid rgba(239,68,68,0.3)" : "1px solid rgba(0,121,192,0.2)",
                      borderRadius: "6px",
                      padding: "0.5rem 0.75rem",
                      cursor: "pointer",
                      color: isFavorite(title) ? "#ef4444" : "#0079C0",
                      fontSize: "1rem",
                      lineHeight: 1,
                      transition: "all 0.2s ease",
                    }}
                  >
                    {isFavorite(title) ? "♥" : "♡"}
                  </button>
                  <button
                    onClick={() => toggleExpand(title)}
                    style={{
                      background: "rgba(0, 121, 192, 0.1)",
                      border: "none",
                      borderRadius: "6px",
                      padding: "0.5rem",
                      cursor: "pointer",
                      color: "#0079C0",
                      fontSize: "0.9rem",
                      fontWeight: "500",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {expandedTitles[title] ? "Hide" : "Show"} Details
                  </button>
                </div>
              </div>

              <AnimatePresence>
                {expandedTitles[title] && (
                  <div style={{ overflow: "hidden" }}>
                    {/* Shared metadata — shown once */}
                    <div style={{
                      borderTop: "1px solid #e5e7eb",
                      paddingTop: "0.75rem",
                      marginTop: "0.75rem",
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: "0.5rem",
                      fontSize: "0.85rem",
                      color: "#374151",
                    }}>
                      <div><strong>Authors:</strong> {sanitize(studies[0].Authors)}</div>
                      <div><strong>Journal:</strong> {sanitize(studies[0].Journal)}</div>
                      <div><strong>Year:</strong> {(studies[0].Year_of_publication || "—").toString().replace(/,/g, "")}</div>
                      <div><strong>Category:</strong> {sanitize(studies[0].Age_category)}</div>
                      <div><strong>Epilepsy Type:</strong> {sanitize(studies[0].Epilepsy_type)}</div>
                      <div><strong>Sample Size:</strong> {sanitize(studies[0].Sample_size_Patients)} patients</div>
                    </div>

                    {/* One row per comparison type */}
                    {selectedIds.size > 0 && (
                      <p style={{ fontSize: "0.75rem", color: "#6b7280", marginTop: "0.75rem", marginBottom: "0.25rem" }}>
                        Check comparisons to include in cross-comparison export
                      </p>
                    )}
                    <div style={{ marginTop: "0.5rem", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                      {studies.map((study, studyIndex) => (
                        <div
                          key={studyIndex}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            padding: "0.5rem 0.75rem",
                            borderRadius: "6px",
                            background: selectedIds.has(study.id) ? "rgba(0,121,192,0.1)" : "rgba(0, 121, 192, 0.06)",
                            border: selectedIds.has(study.id) ? "1px solid rgba(0,121,192,0.35)" : "1px solid rgba(0, 121, 192, 0.12)",
                            transition: "background 0.15s, border 0.15s",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedIds.has(study.id)}
                            onChange={() => toggleSelectId(study.id)}
                            style={{ width: 14, height: 14, cursor: "pointer", accentColor: "#0079C0", flexShrink: 0 }}
                            title="Select this comparison for cross-comparison export"
                          />
                          <Link
                            to={`/study/${study.id}`}
                            style={{
                              flex: 1,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              textDecoration: "none",
                              color: "#1f2937",
                              fontSize: "0.875rem",
                            }}
                          >
                            <span><strong>Comparison:</strong> {sanitize(study.Comparison)}</span>
                            <span style={{ color: "#0079C0", fontWeight: "500", whiteSpace: "nowrap", marginLeft: "1rem" }}>View →</span>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </AnimatePresence>

        {/* Enhanced Pagination */}
        {totalUnique > studiesPerPage && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "0.5rem",
              marginTop: "2rem",
            }}
          >
            {(() => {
              const totalPages = Math.ceil(totalUnique / studiesPerPage);
              const maxButtons = 10;
              let start = Math.max(1, currentPage - Math.floor(maxButtons / 2));
              let end = start + maxButtons - 1;
              if (end > totalPages) {
                end = totalPages;
                start = Math.max(1, end - maxButtons + 1);
              }
              const pageNumbers = [];
              if (start > 1) {
                pageNumbers.push(1);
                if (start > 2) pageNumbers.push('ellipsis-start');
              }
              for (let i = start; i <= end; i++) {
                pageNumbers.push(i);
              }
              if (end < totalPages) {
                if (end < totalPages - 1) pageNumbers.push('ellipsis-end');
                pageNumbers.push(totalPages);
              }
              return pageNumbers.map((num, idx) => {
                if (num === 'ellipsis-start' || num === 'ellipsis-end') {
                  return (
                    <span key={num + idx} style={{ padding: '0 0.5rem', color: '#888', fontSize: '1.2rem' }}>…</span>
                  );
                }
                return (
                  <button
                    key={num}
                    onClick={() => setCurrentPage(num)}
                    style={{
                      padding: "0.75rem 1rem",
                      border: currentPage === num ? "none" : "1px solid #d1d5db",
                      borderRadius: "8px",
                      background: currentPage === num 
                        ? "linear-gradient(135deg, #0079C0 0%, #005f9e 100%)" 
                        : "white",
                      color: currentPage === num ? "white" : "#374151",
                      cursor: "pointer",
                      fontWeight: "500",
                      transition: "all 0.3s ease",
                      boxShadow: currentPage === num 
                        ? "0 4px 15px rgba(0, 121, 192, 0.3)" 
                        : "0 1px 3px rgba(0, 0, 0, 0.1)",
                    }}
                  >
                    {num}
                  </button>
                );
              });
            })()}
          </div>
        )}

        {/* Results Summary */}
        <div
          style={{
            textAlign: "center",
            marginTop: "2rem",
            padding: "1rem",
            background: "rgba(255, 255, 255, 0.8)",
            borderRadius: "8px",
            color: "#6b7280",
            fontSize: "0.9rem",
          }}
        >
          Showing {indexOfFirstStudy + 1}-{Math.min(indexOfLastStudy, filtered.length)} of {filtered.length} results
        </div>
      </div>
      {/* Responsive styles */}
      <style>{`
        @media (max-width: 900px) {
          .search-flex-root {
            flex-direction: column;
            gap: 1.5rem;
          }
          .search-sidebar {
            width: 100%;
            min-width: 0;
            position: static;
            margin-bottom: 0;
          }
          .search-main {
            width: 100%;
            min-width: 0;
            margin-bottom: 0;
          }
        }
      `}</style>
    </div>
  );
}